import React, {FC, useState, useEffect} from "react"
import styled, {css} from "styled-components"
import moment from "moment"
import {Row} from "react-bootstrap"

import {Body, H1, H5} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import {Input} from "@irati/ui/src/FormItem"
import {AU} from "country-flag-icons/react/3x2"
import ga4Tracking from "@irati-web/tracking"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import {useTheme} from "@irati/ui/src/global/useTheme"
import {Card} from "@irati/ui/src/Layout"
import {useAddCompany, useListCompanies} from "@irati/signup/hooks"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {SignUp, SignUpOutcomeIndicator, SignUpStage} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import {hangPromise} from "@irati/utils/promise"
import intercom from "@irati/utils/intercom"
import ToastAlert from "@irati/ui/src/ToastAlert"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import {UseQueriesResult} from "@irati/utils/services/apollo"
import FormattedMessage, {useIntl} from "@irati/utils/lang"

import Spinner from "@irati/ui/src/Loading/Spinner"
import BusinessReviewed from "../../components/SignUp/Cards/BusinessReviewed"
import BusinessSelected from "../../components/SignUp/Cards/BusinessSelected"
import ReviewingYourDetails from "../../components/SignUp/Cards/ReviewingYourDetails"
import NorthrowLink from "../../components/SignUp/NorthrowLink"
import SaveAndProgressFooter from "../../components/SignUp/SaveAndProgressFooter"
import {StyledCard, StyledIcon, StyledTopCard} from "../../components/SignUp/styled"

import {WelcomeMobileButton} from "./Welcome"

const StyledFlex = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.desktop`
      flex-direction: column;
    `}
  `}
`

const CountryWrapper = styled.div`
  ${({theme}) => css`
    position: relative;
    input {
      padding-left: ${theme.spacing.seven};
    }
    div {
      margin-bottom: 0;
    }
    ${theme.mediaQueries.maxWidthMedia.desktop`
      .form-group {
        width: 100%;
      }
    `}
  `}
`

const FlagWrapper = styled.div`
  svg {
    height: 32px;
    width: 32px;
    position: absolute;
    top: 36px;
    left: 32px;
    margin-right: 16px;
  }
`

const CompanyDetails = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.tablet`
      flex-direction: column;
      gap: 8px;
    `}
  `}
`

const CompanyDetailsWidth = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.tablet`
      width: 100%;
    `}
  `}
`

const CompanyButtonsDesktop = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.desktop`
      display: none;
    `}
  `}
`

const CompanyButtonsMobile = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.minWidthMedia.desktop`
      display: none;
    `}
  `}
`

interface Props {
  signUp: UseQueriesResult<SignUp>
  onKYBCompleted: () => void
}

const KnowYourBusiness: FC<Props> = ({signUp, onKYBCompleted}) => {
  const {uuid} = signUp?.data || {}
  const {companyName, emailAddress: email} = signUp?.data.enquiry

  const [companyId, setCompanyId] = useState<string>()
  const [company, setCompany] = useState(null)
  const [errorMessage, setErrorMessage] = useState<boolean | string>(false)
  const [variables, setVariables] = useState({})
  const [showLoading, setShowLoading] = useState(false)
  const [logEvent, setLogEvent] = useState(true)

  const [showReviewing, setShowReviewing] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const theme = useTheme()
  const {formatMessage} = useIntl()

  const [listCompanies, {data, error, loading}] = useListCompanies(variables, {fetchPolicy: "network-only"})
  const [addCompany, addCompanyApollo] = useAddCompany({variables: {uuid, company}})

  useEffect(() => {
    if (!data) return
    const searchedCompany = data?.length && data.find((c) => c.number === companyId)

    if (searchedCompany) {
      setCompany(searchedCompany)
    } else {
      setErrorMessage(formatMessage({id: "platform.selfSignUp.kyb.noCompanyDetails"}))
    }
  }, [data])

  useEffect(() => {
    if (error) {
      setErrorMessage(formatMessage({id: "platform.selfSignUp.kyb.noCompanyDetails"}))
    }
  }, [error])

  const search = () => {
    setErrorMessage(false)
    setCompany(null)
    if (companyName && companyId) {
      setVariables({
        uuid,
        filter: {
          name: companyName,
          number: companyId,
          country: "AU"
        }
      })
      listCompanies()
    } else {
      setErrorMessage(formatMessage({id: "platform.selfSignUp.kyb.companyError"}))
    }
  }

  useEffect(() => {
    if (addCompanyApollo?.loading) {
      setShowLoading(true)
    }
  }, [addCompanyApollo?.loading])

  useEffect(() => {
    if (addCompanyApollo?.error) {
      setShowLoading(false)
      setShowReviewing(true)
    }
  }, [addCompanyApollo?.error])

  useEffect(() => {
    if (addCompanyApollo?.data) {
      signUp.startPolling(10000)
    }
  }, [addCompanyApollo?.data])

  useEffect(() => {
    const stageHasProgressed = signUp?.data?.stage !== SignUpStage.SignUpStageCompany
    const amberOrRedAlert =
      signUp?.data?.companyCheck?.outcome?.indicator &&
      signUp?.data?.companyCheck?.outcome?.indicator !== SignUpOutcomeIndicator.CheckIndicatorGreen

    if (stageHasProgressed || amberOrRedAlert) {
      setShowLoading(false)
      signUp?.stopPolling()
      if (stageHasProgressed) {
        setShowSuccess(true)
      } else if (amberOrRedAlert) {
        setShowReviewing(true)
      }
    }
  }, [signUp])

  useEffect(() => {
    ga4Tracking.trackEventWithStore("kyb_viewed", "sbs_params", {step: "3", email, page: "kyb_page"})
  }, [])

  const handleOpenIntercom = () => {
    intercom.addCustomMessage(formatMessage({id: "platform.selfSignUp.kyb.intercomMsg"}))
  }

  if (showLoading) {
    if (logEvent) {
      ga4Tracking.trackEventWithStore("kyb_started", "sbs_params", {
        step: "3.1",
        kyb_start_time: moment().format("DD/MM/YYYY, h:mm:ss A"),
        email,
        page: "kyb_page"
      })
      setLogEvent(false)
    }
    return <ReviewingYourDetails />
  }

  if (showSuccess) {
    ga4Tracking.trackEventWithStore("kyb_submitted", "sbs_params", {step: "3.3", email, page: "kyb_page"})
    return (
      <>
        <BusinessReviewed onClick={onKYBCompleted} />
        <SaveAndProgressFooter onConfirm={onKYBCompleted} confirmText={formatMessage({id: "platform.button.continue"})} />
      </>
    )
  }

  if (showReviewing) {
    ga4Tracking.trackEventWithStore("kyb_started", "sbs_params", {
      step: "3.2",
      kyb_start_time: moment().format("DD/MM/YYYY, h:mm:ss A"),
      email,
      page: "kyb_page",
      action: "company_selected"
    })
    return (
      <>
        <BusinessSelected />
        <SaveAndProgressFooter />
      </>
    )
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      triggerSuccessToast(formatMessage({id: "platform.selfSignUp.welcome.copyLinkMessage"}))
    })
  }

  return (
    <>
      <ToastAlert />
      <StyledTopCard>
        <Flex marginBottom={2}>
          <StyledIcon icon={IconTypes.circleAlertFill} size={20} />
          <Body weight="bold" color={theme.colors.primary2} marginLeft maxWidth="100%">
            <FormattedMessage id="platform.selfSignUp.kyb.title" />
          </Body>
        </Flex>
        <CompanyDetails gap={4} marginLeft={2}>
          <Flex gap={1} alignItems="center">
            <img src={formatMessage({id: "platform.selfSignUp.kyb.img"})} height={32} width={32} alt="companyTradingName" />
            <Body>
              <FormattedMessage id="platform.selfSignUp.kyb.subTitle" />
            </Body>
          </Flex>
          <Flex gap={1} alignItems="center">
            <img src={formatMessage({id: "platform.selfSignUp.kyb.img2"})} height={32} width={32} alt="companyRegistrationNumber" />
            <Body>
              <FormattedMessage id="platform.selfSignUp.kyb.registration" />
            </Body>
          </Flex>
        </CompanyDetails>
      </StyledTopCard>
      <StyledCard>
        <H1 marginBottom={1} color={theme.colors.primary2}>
          <FormattedMessage id="platform.selfSignUp.kyb.companyDetails" />
        </H1>
        <Body small maxWidth="100%" color={theme.colors.primary2} marginBottom={2}>
          <FormattedMessage id="platform.selfSignUp.kyb.companyDetailsInfo" />
        </Body>
        {errorMessage && <Alert type={AlertTypes.error} title={errorMessage} onHide={() => setErrorMessage(false)} />}
        <StyledFlex gap={1}>
          <Row>
            <CountryWrapper>
              <Input
                label="platform.BUregion.country"
                name="signUpCountry"
                type="text"
                value={formatMessage({id: "platform.selfSignUp.kyb.defaultCountry"})}
                disabled
              />
              <FlagWrapper>
                <AU />
              </FlagWrapper>
            </CountryWrapper>
          </Row>

          <Row>
            <Input label="platform.selfSignUp.kyb.companyName" name="signUpCompanyName" type="text" value={companyName} disabled />
          </Row>
          <Row>
            <Input
              label="platform.selfSignUp.kyb.registration"
              name="signUpCompanyNumber"
              type="text"
              value={companyId}
              onChange={setCompanyId}
            />
          </Row>
        </StyledFlex>
        <CompanyButtonsMobile marginTop marginBottom={1}>
          <Button full onClick={search}>
            <FormattedMessage id="platform.selfSignUp.kyb.searchForCompany" />
          </Button>
        </CompanyButtonsMobile>
        <CompanyButtonsDesktop justifyContent="space-between">
          <Button small tertiary onClick={handleOpenIntercom}>
            <FormattedMessage id="platform.selfSignUp.kyb.noCompany" />
          </Button>
          <Button small onClick={search}>
            <FormattedMessage id="platform.selfSignUp.kyb.searchForCompany" />
          </Button>
        </CompanyButtonsDesktop>
        {loading && (
          <Flex direction="column">
            <Loading load={hangPromise()} Loader={() => <Spinner />} />
            <Body large textAlign="center" maxWidth="100%">
              <FormattedMessage id="platform.selfSignUp.kyb.fewMinutes" />
            </Body>
          </Flex>
        )}
        {company && (
          <>
            <Card bgColor={theme.colors.secondary1_50} marginTop={2}>
              <CompanyDetails>
                <CompanyDetailsWidth direction="column" width="50%">
                  <Flex gap={1} alignItems="center" marginBottom={2}>
                    <H5 color={theme.colors.primary2}>
                      <FormattedMessage id="platform.selfSignUp.kyb.companyName" />:
                    </H5>
                    <Body small color={theme.colors.primary2}>
                      {company.name}
                    </Body>
                  </Flex>
                  <Flex gap={1} alignItems="center">
                    <H5 color={theme.colors.primary2}>
                      <FormattedMessage id="platform.selfSignUp.kyb.registration" />:
                    </H5>
                    <Body small color={theme.colors.primary2}>
                      {company.number}
                    </Body>
                  </Flex>
                </CompanyDetailsWidth>
                <CompanyDetailsWidth gap={1} width="50%">
                  <H5 noWrap color={theme.colors.primary2}>
                    <FormattedMessage id="platform.selfSignUp.kyb.companyAddress" />:
                  </H5>
                  <Body small color={theme.colors.primary2}>
                    {company.address.nameOrNumber ? `${company.address.nameOrNumber}, ` : ""}
                    {company.address.postcode ? `${company.address.postcode}, ` : ""}
                    {company.address.town ? `${company.address.town}, ` : ""}
                    <FormattedMessage id="platform.selfSignUp.kyb.defaultCountry" />
                  </Body>
                </CompanyDetailsWidth>
              </CompanyDetails>
            </Card>
            <CompanyButtonsMobile marginBottom={3} marginTop={2}>
              <Button primary onClick={addCompany} full>
                <FormattedMessage id="platform.selfSignUp.kyb.verifyCompany" />
              </Button>
            </CompanyButtonsMobile>
          </>
        )}
        <CompanyButtonsMobile justifyContent="center" marginTop={3} marginBottom={3}>
          <Button tertiary>
            <FormattedMessage id="platform.selfSignUp.kyb.noCompany" />
          </Button>
        </CompanyButtonsMobile>
        <WelcomeMobileButton tertiary full marginBottom={3} onClick={handleCopyLink}>
          <Icon icon={IconTypes.files} size={16} marginRight />
          <FormattedMessage id="platform.selfSignUp.welcome.popUp.copyLink" />
        </WelcomeMobileButton>
        <Flex justifyContent="center" marginTop={2} alignItems="center" gap={0.5}>
          <Icon color={theme.colors.greys.grey50} size={10} icon={IconTypes.padlock} />
          <NorthrowLink />
        </Flex>
      </StyledCard>
      <SaveAndProgressFooter
        disabledSave={!company}
        onConfirm={addCompany}
        confirmText={formatMessage({id: "platform.selfSignUp.kyb.verifyCompany"})}
      />
    </>
  )
}

export default KnowYourBusiness
